import type { ReactElement } from 'react';
import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Loader,
  User,
  Store,
  UserCheck,
  UserX,
  BarChart3,
  MapPin,
  X,
  AlertCircle,
  RefreshCw,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { sellersService } from '../../services/api';
import { useToast } from '../../hooks/useToast';
import ToastContainer from '../../components/ui/ToastContainer';
import type {
  Seller,
  PaginatedResponse,
  SellerFilters,
  CreateSellerRequest,
  UpdateSellerRequest,
  RepresentativeOption,
  StoreOption,
  UserOption,
  SellerStats,
} from '../../types/api';

const useDebounce = (value: string, delay: number): string => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const SellersManagement = (): ReactElement => {
  const [sellers, setSellers] = useState<Seller[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [representatives, setRepresentatives] = useState<
    RepresentativeOption[]
  >([]);
  const [stores, setStores] = useState<StoreOption[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [stats, setStats] = useState<SellerStats | null>(null);
  const [auxiliaryLoading, setAuxiliaryLoading] = useState(true); // ✅ Loading separado

  // ✅ Estados de filtros con debounce
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300); // ✅ 300ms debounce
  const [selectedZone, setSelectedZone] = useState<string>('');
  const [selectedRepresentative, setSelectedRepresentative] =
    useState<string>('');
  const [selectedActiveStatus, setSelectedActiveStatus] = useState<
    'active' | 'inactive' | 'all'
  >('all');

  // Estados de modal
  const [showModal, setShowModal] = useState(false);
  const [editingSeller, setEditingSeller] = useState<Seller | null>(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [formData, setFormData] = useState({
    userId: '',
    representativeId: '',
    name: '',
    storeId: '',
  });

  // Toast hook
  const { addToast, toasts, removeToast } = useToast();

  // Verificación de permisos
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') ?? 'null') as {
        role?: string;
      } | null;
    } catch {
      return null;
    }
  }, []);

  const canManageSellers =
    user?.role === 'SYSTEM_ADMIN' ||
    user?.role === 'ADMIN' ||
    user?.role === 'GENERAL_MANAGER';

  const hasActiveFilters = useMemo(() => {
    return !!(
      searchTerm ||
      selectedZone ||
      selectedRepresentative ||
      selectedActiveStatus !== 'all'
    );
  }, [searchTerm, selectedZone, selectedRepresentative, selectedActiveStatus]);

  const availableZones = useMemo(() => {
    const zones = new Set(representatives.map((r) => r.zone.name));
    return Array.from(zones).sort();
  }, [representatives]);

  const loadSellers = useCallback(async (): Promise<void> => {
    if (!canManageSellers) return;

    try {
      setLoading(true);
      setError(null);

      const queryFilters: SellerFilters = {
        page: currentPage,
        limit: itemsPerPage === 50 ? 100 : itemsPerPage,
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(selectedZone && { representativeZoneId: selectedZone }),
        ...(selectedRepresentative && {
          representativeId: selectedRepresentative,
        }),
        ...(selectedActiveStatus !== 'all' && {
          active: selectedActiveStatus === 'active',
        }),
        sortBy: 'name',
        sortOrder: 'asc',
      };

      const response: any = await sellersService.getAll(queryFilters);

      setSellers(response.data || []);
      setTotalPages(response.totalPages || 0);
      setTotal(response.total || 0);
    } catch (err) {
      setError('Error al cargar vendedores');
      addToast('error', 'Error al cargar la lista de vendedores');
      console.error('Error loading sellers:', err);
    } finally {
      setLoading(false);
    }
  }, [
    canManageSellers,
    currentPage,
    itemsPerPage,
    debouncedSearchTerm,
    selectedZone,
    selectedRepresentative,
    selectedActiveStatus,
    addToast,
  ]);

  const loadAuxiliaryData = useCallback(async (): Promise<void> => {
    if (!canManageSellers) return;

    try {
      setAuxiliaryLoading(true);

      const [repsData, storesData, usersData, statsData] = await Promise.all([
        sellersService.getRepresentatives(),
        sellersService.getStores(),
        sellersService.getAvailableUsers(),
        sellersService.getStats(),
      ]);

      setRepresentatives(repsData || []);
      setStores(storesData || []);
      setUsers(usersData || []);
      setStats(statsData || null);
    } catch (err) {
      console.error('Error loading auxiliary data:', err);
      addToast('error', 'Error al cargar datos auxiliares');
    } finally {
      setAuxiliaryLoading(false);
    }
  }, [canManageSellers, addToast]);

  useEffect(() => {
    if (canManageSellers) {
      void loadAuxiliaryData();
    }
  }, [canManageSellers]);
  useEffect(() => {
    void loadSellers();
  }, [loadSellers]);

  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [
    debouncedSearchTerm,
    selectedZone,
    selectedRepresentative,
    selectedActiveStatus,
  ]);

  // Reset página cuando cambia items per page
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [itemsPerPage]);

  // Funciones auxiliares
  const getUserStatusColor = (active: boolean): string => {
    return active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800';
  };

  const getZoneDisplayName = (zoneName: string): string => {
    const zoneNames = {
      NORTE: 'Norte',
      CENTRO: 'Centro',
      SUR: 'Sur',
    };
    return zoneNames[zoneName as keyof typeof zoneNames] || zoneName;
  };

  const getZoneBadgeColor = (zoneName: string): string => {
    const colors = {
      NORTE: 'bg-blue-100 text-blue-800',
      CENTRO: 'bg-green-100 text-green-800',
      SUR: 'bg-orange-100 text-orange-800',
    };
    return (
      colors[zoneName as keyof typeof colors] || 'bg-gray-100 text-gray-800'
    );
  };

  // ===== HANDLERS PARA CRUD =====
  const handleCreateSeller = (): void => {
    resetForm();
    setEditingSeller(null);
    setShowModal(true);
  };

  const handleEditSeller = (seller: Seller): void => {
    setEditingSeller(seller);
    setFormData({
      name: seller.name,
      storeId: seller.store.id,
      representativeId: seller.representative?.id ?? '',
      userId: seller.user?.id ?? '',
    });
    setShowModal(true);
  };

  const handleSaveSeller = async (): Promise<void> => {
    if (!formData.userId) {
      addToast('error', 'Debe seleccionar un usuario');
      return;
    }
    if (!formData.representativeId) {
      addToast('error', 'Debe seleccionar un representante');
      return;
    }
    if (!formData.name) {
      addToast('error', 'El nombre del vendedor es obligatorio');
      return;
    }
    if (!formData.storeId) {
      addToast('error', 'Debe seleccionar una tienda');
      return;
    }

    setModalLoading(true);
    try {
      if (editingSeller) {
        // ENVIAR TODOS LOS CAMPOS AL ACTUALIZAR
        await sellersService.update(editingSeller.id, {
          userId: formData.userId,
          representativeId: formData.representativeId,
          name: formData.name,
          storeId: formData.storeId,
        });
        addToast('success', 'Asignación actualizada correctamente');
      } else {
        // ENVIAR TODOS LOS CAMPOS AL CREAR
        await sellersService.create({
          userId: formData.userId,
          representativeId: formData.representativeId,
          name: formData.name,
          storeId: formData.storeId,
        });
        addToast('success', 'Vendedor asignado correctamente');
      }

      setShowModal(false);
      resetForm();
      await loadSellers();
    } catch (error: any) {
      console.error('Error al guardar:', error);
      const message =
        error.response?.data?.message || 'Error al procesar asignación';
      addToast('error', message);
    } finally {
      setModalLoading(false);
    }
  };

  const handleToggleSellerStatus = async (seller: Seller): Promise<void> => {
    try {
      if (seller.active) {
        await sellersService.deactivate(seller.id);
        addToast('success', 'Vendedor desactivado correctamente');
      } else {
        await sellersService.activate(seller.id);
        addToast('success', 'Vendedor activado correctamente');
      }
      await Promise.all([loadSellers(), loadAuxiliaryData()]);
    } catch (error: any) {
      console.error('Error al cambiar estado del vendedor:', error);
      const message =
        error.response?.data?.message || 'Error al cambiar estado del vendedor';
      addToast('error', message);
    }
  };

  const handleDeleteSeller = async (seller: Seller): Promise<void> => {
    if (
      !confirm(
        `¿Estás seguro de que quieres eliminar la asignación de ${seller.name}?`
      )
    ) {
      return;
    }

    try {
      await sellersService.delete(seller.id);
      addToast('success', 'Asignación eliminada correctamente');
      await loadSellers();
    } catch (error: any) {
      addToast('error', 'Error al eliminar asignación', error);
    }
    addToast('error', 'Error al eliminar asignación');
  };

  const resetForm = (): void => {
    setFormData({
      userId: '',
      representativeId: '',
      name: '',
      storeId: '',
    });
  };

  const handlePageChange = useCallback((page: number): void => {
    setCurrentPage(page);
  }, []);

  const handleItemsPerPageChange = useCallback(
    (newItemsPerPage: number): void => {
      setItemsPerPage(newItemsPerPage);
      // currentPage se resetea automáticamente por el useEffect
    },
    []
  );

  // HANDLERS DE FILTROS OPTIMIZADOS
  const handleSearchChange = useCallback((value: string): void => {
    setSearchTerm(value);
    // La búsqueda se ejecutará automáticamente con debounce
  }, []);

  const handleZoneChange = useCallback((value: string): void => {
    setSelectedZone(value);
  }, []);

  const handleRepresentativeChange = useCallback((value: string): void => {
    setSelectedRepresentative(value);
  }, []);

  const handleActiveStatusChange = useCallback(
    (value: 'active' | 'inactive' | 'all'): void => {
      setSelectedActiveStatus(value);
    },
    []
  );

  const handleClearFilters = useCallback((): void => {
    setSearchTerm('');
    setSelectedZone('');
    setSelectedRepresentative('');
    setSelectedActiveStatus('all');
    // currentPage se resetea automáticamente
  }, []);

  const handleRefresh = useCallback((): void => {
    loadSellers();
    loadAuxiliaryData();
  }, [loadSellers, loadAuxiliaryData]);

  // ===== VERIFICACIÓN DE PERMISOS =====
  if (!canManageSellers) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <UserX className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Acceso Denegado
              </h3>
              <p className="mt-2 text-sm text-red-700">
                No tienes permisos para gestionar asignaciones de vendedores.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header con estadísticas */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Asignaciones de Vendedores por Representante
          </h2>
          <p className="text-sm text-gray-600">
            {sellers.length} de {total} vendedores
            {stats && (
              <span className="ml-2">
                • {stats.active} activos • {stats.total - stats.active}{' '}
                inactivos
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCreateSeller}
            disabled={auxiliaryLoading}
            className="flex items-center gap-2 bg-primary-dark text-white px-4 py-2 rounded-lg hover:bg-primary-accent transition-colors disabled:opacity-50"
          >
            {auxiliaryLoading ? (
              <Loader className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            Asignar Vendedor
          </button>
        </div>
      </div>

      {/* Panel de estadísticas */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <BarChart3 className="w-8 h-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.total}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <UserCheck className="w-8 h-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Activos</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.active}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <UserX className="w-8 h-8 text-red-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Inactivos</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.total - stats.active}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-600">
                Por Representante
              </p>
              <div className="text-sm">
                <div className="flex justify-between">
                  <span>Representantes</span>
                  <span className="font-medium">
                    {stats.byRepresentative.length}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Promedio/Rep</span>
                  <span className="font-medium">
                    {stats.byRepresentative.length > 0
                      ? Math.round(stats.total / stats.byRepresentative.length)
                      : 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Búsqueda */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Buscar vendedores..."
                value={searchTerm}
                onChange={(e) => {
                  handleSearchChange(e.target.value);
                }}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent w-full"
              />
              {/* Indicador de debounce */}
              {searchTerm !== debouncedSearchTerm && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <Loader className="w-4 h-4 animate-spin text-blue-500" />
                </div>
              )}
            </div>
          </div>

          {/* Zona */}
          <div className="relative w-full md:w-48">
            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <select
              value={selectedZone}
              onChange={(e) => {
                handleZoneChange(e.target.value);
              }}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent w-full appearance-none"
            >
              <option value="">Todas las zonas</option>
              {availableZones.map((zone) => (
                <option key={zone} value={zone}>
                  {getZoneDisplayName(zone)}
                </option>
              ))}
            </select>
          </div>

          {/* Representante */}
          <div className="relative w-full md:w-56">
            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <select
              value={selectedRepresentative}
              onChange={(e) => {
                handleRepresentativeChange(e.target.value);
              }}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent w-full appearance-none"
            >
              <option value="">Todos los representantes</option>
              {representatives.map((rep) => (
                <option key={rep.id} value={rep.id}>
                  {rep.user.name}
                </option>
              ))}
            </select>
          </div>

          {/* Estado */}
          <div className="relative w-full md:w-52">
            <UserCheck className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <select
              value={selectedActiveStatus}
              onChange={(e) => {
                handleActiveStatusChange(
                  e.target.value as 'active' | 'inactive' | 'all'
                );
              }}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-light focus:border-transparent w-full appearance-none"
            >
              <option value="all">Todos</option>
              <option value="active">Solo activos</option>
              <option value="inactive">Solo inactivos</option>
            </select>
          </div>

          {/* Botón Refresh - Solo icono */}
          <button
            onClick={handleRefresh}
            disabled={loading || auxiliaryLoading}
            className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            title="Actualizar"
          >
            <RefreshCw
              className={`h-4 w-4 ${
                loading || auxiliaryLoading ? 'animate-spin' : ''
              }`}
            />
          </button>
        </div>

        {/* Active Filters Badges */}
        {hasActiveFilters && (
          <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-gray-200">
            <span className="text-sm text-gray-600">Filtros activos:</span>

            {searchTerm && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                Búsqueda: "{searchTerm}"
                <button
                  onClick={() => {
                    setSearchTerm('');
                  }}
                  className="hover:bg-blue-200 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            {selectedZone && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                Zona: {getZoneDisplayName(selectedZone)}
                <button
                  onClick={() => {
                    setSelectedZone('');
                  }}
                  className="hover:bg-green-200 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            {selectedRepresentative && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                Representante:{' '}
                {
                  representatives.find((r) => r.id === selectedRepresentative)
                    ?.user.name
                }
                <button
                  onClick={() => {
                    setSelectedRepresentative('');
                  }}
                  className="hover:bg-purple-200 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            {selectedActiveStatus !== 'all' && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                Estado:{' '}
                {selectedActiveStatus === 'active' ? 'Activos' : 'Inactivos'}
                <button
                  onClick={() => {
                    setSelectedActiveStatus('all');
                  }}
                  className="hover:bg-orange-200 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            <button
              onClick={handleClearFilters}
              className="text-xs text-gray-600 hover:text-gray-900 underline ml-2"
            >
              Limpiar todos los filtros
            </button>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <div className="text-red-800">
                <strong>Error:</strong> {error}
              </div>
              <button
                onClick={handleRefresh}
                className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
              >
                Reintentar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabla principal */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Vendedor
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Tienda
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Representante Asignado
                </th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  Estado
                </th>
                <th className="text-center py-3 px-4 font-medium text-gray-700">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-gray-500">
                    <div className="flex items-center justify-center">
                      <Loader className="h-6 w-6 animate-spin text-blue-600 mr-2" />
                      <span>Cargando vendedores...</span>
                    </div>
                  </td>
                </tr>
              ) : sellers.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-gray-500">
                    <div>
                      <div className="text-xl mb-2">👥</div>
                      <div>No se encontraron vendedores</div>
                      <div className="text-sm text-gray-400 mt-1">
                        {searchTerm ||
                        selectedZone ||
                        selectedRepresentative ||
                        selectedActiveStatus !== 'all'
                          ? 'Intenta ajustar los filtros'
                          : 'Comienza asignando tu primer vendedor'}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                sellers.map((seller) => (
                  <tr
                    key={seller.id}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <User className="w-8 h-8 text-gray-400 mr-3" />
                        <div>
                          <div className="text-left font-medium text-gray-900">
                            {seller.name}
                          </div>
                          {seller.user && (
                            <div className="text-left text-sm text-gray-600">
                              #{seller.user.employeeNumber}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <Store className="h-4 w-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-left text-sm text-gray-900">
                            {seller.store.name}
                          </div>
                          <div className="text-left text-sm text-gray-500">
                            {seller.store.city.name} • {seller.store.chain.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {seller.representative ? (
                        <div>
                          <div className="text-left text-sm text-gray-900">
                            {seller.representative.user.name}
                          </div>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getZoneBadgeColor(
                              seller.representative.zone.name
                            )}`}
                          >
                            <MapPin className="w-3 h-3 mr-1" />
                            {getZoneDisplayName(
                              seller.representative.zone.name
                            )}
                          </span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500 italic">
                          Sin asignar
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getUserStatusColor(
                          seller.active
                        )}`}
                      >
                        {seller.active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center justify-end gap-2 pr-2">
                        {/* Botón Editar */}
                        <button
                          onClick={() => {
                            handleEditSeller(seller);
                          }}
                          className="p-2 text-primary-medium hover:text-primary-dark rounded-lg transition-colors"
                          title="Editar asignación"
                        >
                          <Edit className="h-4 w-4" />
                        </button>

                        {/* Botón Activar/Desactivar */}
                        <button
                          onClick={() => {
                            void handleToggleSellerStatus(seller);
                          }}
                          className={`p-2 rounded-lg transition-colors ${
                            seller.active
                              ? 'text-green-600 hover:bg-green-50'
                              : 'text-gray-400 hover:bg-gray-50'
                          }`}
                          title={seller.active ? 'Desactivar' : 'Activar'}
                        >
                          {seller.active ? (
                            <CheckCircle className="h-4 w-4" />
                          ) : (
                            <XCircle className="h-4 w-4" />
                          )}
                        </button>

                        {/* Botón Eliminar */}
                        <button
                          onClick={() => {
                            void handleDeleteSeller(seller);
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Eliminar asignación"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Paginación */}
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="flex items-center gap-4">
                <div className="text-sm text-gray-700">
                  Mostrando {(currentPage - 1) * itemsPerPage + 1} a{' '}
                  {Math.min(currentPage * itemsPerPage, total)} de {total}{' '}
                  resultados
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-700">Mostrar:</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) =>
                      handleItemsPerPageChange(Number(e.target.value))
                    }
                    className="border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-green-950"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                  <span className="text-sm text-gray-700">por página</span>
                </div>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1 || loading}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Anterior
                  </button>

                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => {
                            handlePageChange(pageNum);
                          }}
                          disabled={loading}
                          className={`px-3 py-1 text-sm rounded disabled:opacity-50 ${
                            currentPage === pageNum
                              ? 'bg-primary-accent text-white'
                              : 'border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => {
                      handlePageChange(currentPage + 1);
                    }}
                    disabled={currentPage === totalPages || loading}
                    className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Siguiente
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal mejorado */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-medium text-gray-900">
                {editingSeller ? 'Cambiar Asignación' : 'Asignar Vendedor'}
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                }}
                disabled={modalLoading}
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Usuario existente */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Seleccionar Usuario
                </label>
                <select
                  value={formData.userId}
                  onChange={(e) => {
                    const selectedUserId = e.target.value;
                    const selectedUser = users.find(
                      (u) => u.id === selectedUserId
                    );

                    setFormData((prev) => ({
                      ...prev,
                      userId: selectedUserId,
                      name: selectedUser ? selectedUser.name : '',
                    }));
                  }}
                  disabled={modalLoading}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Seleccionar usuario...</option>
                  {users
                    .filter((u) => u.role === 'SELLER')
                    .map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.name} (#{user.employeeNumber})
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Vendedor
                </label>
                <input
                  type="text"
                  value={formData.name}
                  readOnly={!editingSeller}
                  onChange={(e) => {
                    if (editingSeller) {
                      setFormData((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }));
                    }
                  }}
                  disabled={modalLoading}
                  placeholder={
                    editingSeller
                      ? 'Editar nombre'
                      : 'Se llenará automáticamente'
                  }
                  className={`w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    !editingSeller ? 'bg-gray-50 cursor-not-allowed' : ''
                  }`}
                />
                {!editingSeller && (
                  <p className="mt-1 text-xs text-gray-500">
                    El nombre se toma automáticamente del usuario seleccionado
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tienda <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.storeId}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      storeId: e.target.value,
                    }));
                  }}
                  disabled={modalLoading}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Seleccionar tienda...</option>
                  {stores.map((store) => (
                    <option key={store.id} value={store.id}>
                      {store.name} - {store.city.name} ({store.chain.name})
                    </option>
                  ))}
                </select>
              </div>

              {/* Representante */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Representante
                </label>
                <select
                  value={formData.representativeId}
                  onChange={(e) => {
                    setFormData((prev) => ({
                      ...prev,
                      representativeId: e.target.value,
                    }));
                  }}
                  disabled={modalLoading}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-950"
                >
                  <option value="">Seleccionar representante...</option>
                  {representatives.map((rep) => (
                    <option key={rep.id} value={rep.id}>
                      {rep.user.name} ({rep.zone.name})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3 p-6 border-t">
              <button
                onClick={() => {
                  setShowModal(false);
                }}
                disabled={modalLoading}
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveSeller}
                disabled={modalLoading}
                className="bg-primary-dark text-white rounded-sm px-4 py-2 hover:bg-primary-accent transition-colors duration-200"
              >
                {modalLoading && (
                  <Loader className="h-4 w-4 mr-2 animate-spin" />
                )}
                {editingSeller ? 'Actualizar' : 'Asignar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
};

export default SellersManagement;
